import React, { useState, useEffect } from 'react';
import './AcceptInvitation.css';

// Supabase configuration from environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('Missing Supabase environment variables. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.');
}

// App store links
const IOS_APP_STORE_URL = ''; // Disabled
const ANDROID_PLAY_STORE_URL = 'https://drive.google.com/file/d/1raTTfzHzUD0iOj0cJuUfq0EBZgTVsU_-/view?usp=drive_link';

function AcceptInvitation() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, []);

  const loadInvitation = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      setError('Invalid invitation link. Please check your invitation email.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_invitation_by_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ invitation_token: token })
      });

      if (!response.ok) {
        throw new Error('Failed to load invitation details');
      }

      const data = await response.json();

      if (!data || data.length === 0) {
        setError('Invitation not found or has expired.');
        setLoading(false);
        return;
      }

      const invitationData = data[0];

      if (new Date(invitationData.expires_at) < new Date()) {
        setError('This invitation has expired. Please contact the person who invited you.');
        setLoading(false);
        return;
      }

      if (invitationData.status !== 'pending' && invitationData.status !== 'sent') {
        setError(`This invitation has already been ${invitationData.status}.`);
        setLoading(false);
        return;
      }

      setInvitation(invitationData);
      setLoading(false);

      // Track email click
      await trackEmailClick(token);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation. Please try again.');
      setLoading(false);
    }
  };

  const trackEmailClick = async (token) => {
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/rpc/track_invitation_email_click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ invitation_token: token })
      });
    } catch (err) {
      console.error('Error tracking email click:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setSubmitting(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      const response = await fetch(`${SUPABASE_URL}/functions/v1/accept-invitation-web`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          token: token,
          password: password
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create account');
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to create account. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card">
          <div className="error-message">
            <h2>Oops!</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card">
          <div className="success-section">
            <h2>Welcome to Miyel! 🎉</h2>
            <p>Your account has been created successfully. Now download the Miyel app to start connecting with your village:</p>

            <div className="download-buttons">
              <a href={ANDROID_PLAY_STORE_URL} className="download-btn android" target="_blank" rel="noopener noreferrer">
                <span>📱</span>
                <span>Download for Android</span>
              </a>
            </div>

            <p className="download-note">
              After downloading, sign in with your email and the password you just created.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invitation-container">
      <div className="accept-invitation-card">
        <div className="invitation-header">
          <h1 className="logo">Miyel</h1>
          <h2>Village Invitation</h2>
        </div>

        <div className="invitation-info">
          <div className="village-name">{invitation.village_name}</div>
          <p className="invitation-text">
            <strong>{invitation.inviter_name}</strong> has invited you to join their village community.
          </p>
          <p className="invitation-text">
            Welcome <strong>{invitation.full_name}</strong>!
          </p>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="section-title">Set Your Password</h3>
            <p className="section-description">Create a secure password to access your Miyel account:</p>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                className="form-input"
                required
                minLength="8"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
              <div className="password-requirements">
                At least 8 characters with a mix of letters, numbers, and symbols
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                className="form-input"
                required
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
              />
            </div>

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AcceptInvitation;
